import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Pencil, Power, PowerOff, Tag, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateOutcome,
  useDeactivateOutcome,
  useOutcomeDefinitions,
  useUpdateOutcome,
} from '@/hooks/useOutcomes';
import { usePageTitle } from '@/hooks/usePageTitle';
import { extractErrorMessage } from '@/lib/utils';

import { getOutcomeColorClass, OUTCOME_COLOR_NAMES } from '@repo/shared-types';

import type { OutcomeDefinition, OutcomeDefinitionInput } from '@repo/shared-types';

const FLAG_FIELDS = [
  {
    key: 'counts_as_answered',
    label: 'Counts as answered',
    hint: 'The call reached a person (used by answer-rate metrics and review checks).',
  },
  {
    key: 'expects_data_capture',
    label: 'Expects data capture',
    hint: 'This outcome implies structured data (lead/availability/…) should exist.',
  },
  {
    key: 'is_success',
    label: 'Campaign success',
    hint: 'Terminal success for campaign flows (suppresses follow-up SMS when combined with data capture).',
  },
  {
    key: 'is_retryable',
    label: 'Retryable',
    hint: 'Offered as a default retry trigger for campaigns.',
  },
] as const;

type OutcomeFormState = {
  key: string;
  label: string;
  color: string;
  sort_order: number;
  counts_as_answered: boolean;
  expects_data_capture: boolean;
  is_success: boolean;
  is_retryable: boolean;
};

const emptyForm: OutcomeFormState = {
  key: '',
  label: '',
  color: 'slate',
  sort_order: 0,
  counts_as_answered: false,
  expects_data_capture: false,
  is_success: false,
  is_retryable: false,
};

function formFromDefinition(def: OutcomeDefinition): OutcomeFormState {
  return {
    key: def.key,
    label: def.label,
    color: def.color,
    sort_order: def.sort_order,
    counts_as_answered: def.counts_as_answered,
    expects_data_capture: def.expects_data_capture,
    is_success: def.is_success,
    is_retryable: def.is_retryable,
  };
}

export const OutcomesPage = () => {
  usePageTitle('Call Outcomes');
  const { toast } = useToast();
  const { data: definitions = [], isLoading, error } = useOutcomeDefinitions();
  const createMutation = useCreateOutcome();
  const updateMutation = useUpdateOutcome();
  const deactivateMutation = useDeactivateOutcome();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OutcomeDefinition | null>(null);
  const [form, setForm] = useState<OutcomeFormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (def: OutcomeDefinition) => {
    setEditing(def);
    setForm(formFromDefinition(def));
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editing) {
        // Key is immutable — send only editable fields.
        const input: Partial<OutcomeDefinitionInput> = {
          label: form.label,
          color: form.color,
          sort_order: form.sort_order,
          counts_as_answered: form.counts_as_answered,
          expects_data_capture: form.expects_data_capture,
          is_success: form.is_success,
          is_retryable: form.is_retryable,
        };
        await updateMutation.mutateAsync({ id: editing.id, input });
        toast({ variant: 'success', title: `Outcome ${editing.key} updated` });
      } else {
        await createMutation.mutateAsync({
          key: form.key.trim().toUpperCase().replace(/\s+/g, '_'),
          label: form.label,
          color: form.color,
          sort_order: form.sort_order,
          counts_as_answered: form.counts_as_answered,
          expects_data_capture: form.expects_data_capture,
          is_success: form.is_success,
          is_retryable: form.is_retryable,
        });
        toast({ variant: 'success', title: 'Outcome created' });
      }
      setModalOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (def: OutcomeDefinition) => {
    try {
      if (def.active) {
        await deactivateMutation.mutateAsync(def.id);
        toast({ variant: 'success', title: `Outcome ${def.key} deactivated` });
      } else {
        await updateMutation.mutateAsync({ id: def.id, input: { active: true } });
        toast({ variant: 'success', title: `Outcome ${def.key} reactivated` });
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: extractErrorMessage(err),
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Call Outcomes</h1>
          <p className="text-muted-foreground text-sm">
            The platform-wide outcome vocabulary calls can be tagged with. Reference the keys from
            your prompts and workflows however you like.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Outcome
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Definitions
          </CardTitle>
          <CardDescription>
            System outcomes are written by the telephony layer — their key can&apos;t change and
            they can&apos;t be deactivated. Keys are immutable once created; deactivating hides an
            outcome from pickers without touching historical calls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground py-6 text-center text-sm">Loading…</p>
          ) : error ? (
            <p className="text-destructive py-6 text-center text-sm">
              Failed to load outcomes: {extractErrorMessage(error)}
            </p>
          ) : definitions.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              No outcome definitions yet — run the migration seed or create one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                    <th className="px-3 py-2">Outcome</th>
                    <th className="px-3 py-2">Semantics</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map((def) => (
                    <tr key={def.id} className={`border-b ${def.active ? '' : 'opacity-50'}`}>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`w-fit rounded-full px-2 py-0.5 text-xs font-medium ${getOutcomeColorClass(def.color)}`}
                          >
                            {def.label}
                          </span>
                          <span className="text-muted-foreground font-mono text-xs">{def.key}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex max-w-55 flex-wrap gap-1">
                          {def.counts_as_answered && <Badge variant="outline">answered</Badge>}
                          {def.expects_data_capture && <Badge variant="outline">data</Badge>}
                          {def.is_success && <Badge variant="outline">success</Badge>}
                          {def.is_retryable && <Badge variant="outline">retryable</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <Badge variant={def.active ? 'secondary' : 'outline'} className="w-fit">
                            {def.active ? 'Active' : 'Inactive'}
                          </Badge>
                          {def.is_system && (
                            <Badge variant="outline" className="w-fit">
                              System
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Edit"
                            onClick={() => openEdit(def)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!def.is_system && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={def.active ? 'Deactivate' : 'Reactivate'}
                              onClick={() => handleToggleActive(def)}
                            >
                              {def.active ? (
                                <PowerOff className="h-4 w-4" />
                              ) : (
                                <Power className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog.Root open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
          <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg font-semibold">
                {editing ? `Edit ${editing.key}` : 'New Outcome'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
            <form
              onSubmit={handleSubmit}
              className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pe-1"
            >
              {!editing && (
                <>
                  <div>
                    <label className="text-sm font-medium" htmlFor="outcome-key">
                      Key
                    </label>
                    <input
                      id="outcome-key"
                      type="text"
                      value={form.key}
                      onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                      placeholder="e.g. TEST_DRIVE_BOOKED"
                      className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 font-mono text-sm"
                      required
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      UPPER_SNAKE_CASE, immutable after creation — it&apos;s what gets stored on
                      calls.
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium" htmlFor="outcome-label">
                  Label
                </label>
                <input
                  id="outcome-label"
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Test Drive Booked"
                  className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium" htmlFor="outcome-color">
                    Color
                  </label>
                  <select
                    id="outcome-color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    {OUTCOME_COLOR_NAMES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <span
                    className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getOutcomeColorClass(form.color)}`}
                  >
                    {form.label || 'Preview'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="outcome-sort">
                    Sort order
                  </label>
                  <input
                    id="outcome-sort"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))
                    }
                    className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Semantics</p>
                {FLAG_FIELDS.map((flag) => (
                  <label key={flag.key} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={form[flag.key]}
                      onChange={(e) => setForm((f) => ({ ...f, [flag.key]: e.target.checked }))}
                    />
                    <span>
                      {flag.label}
                      <span className="text-muted-foreground block text-xs">{flag.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Dialog.Close asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create Outcome'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};
